// Reusable "Add to Route" button — drop in anywhere with a leadId. Opens a picker
// of existing routes (marking ones the lead is already on) and can create a new
// route on the spot, then adds the lead to the chosen route.
//
//   <AddToRouteButton leadId={lead.id} />
//   <AddToRouteButton leadId={call.lead_id} variant="plain" onAdded={reload} />
//
// It can also be driven from outside — used by the map view, whose pin popups are
// Google Maps InfoWindow HTML and so can't host a React trigger:
//
//   <AddToRouteButton leadId={id} hideTrigger openSignal={signal} />
//
// Bump `openSignal` (any changing truthy value) to open the picker.
import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FaRoute, FaPlus, FaSpinner, FaCheck, FaCalendarDay, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import { getRoutes, getRoutesByLead, createRoute, addStopsToRoute } from '../routeService';
import { getLeadById } from '../../leads/leadService';
import { getLeadAddresses } from '../../leads/leadAddressService';
import { formatRouteDate, routeDateRelative, isRouteUpcoming } from '../utils';

const Trigger = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  min-height: 40px; padding: 0 14px; border-radius: 10px; cursor: pointer; font-size: 0.85rem; font-weight: 600;
  border: 1px solid ${p => p.theme.colors.inputGray}44; background: transparent; color: ${p => p.theme.colors.textSecondary};
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const RouteRow = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; text-align: left;
  background: ${p => p.theme.colors.background}; border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-radius: 10px; padding: 11px 13px; cursor: pointer; & + & { margin-top: 8px; }
  &:hover:not(:disabled) { border-color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.92rem; }
  .date { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; margin-top: 3px; display: flex; align-items: center; gap: 6px; }
  .added { color: ${p => p.theme.colors.success}; font-size: 0.74rem; font-weight: 700; display: inline-flex; align-items: center; gap: 5px; }
`;

const NewBox = styled.div`
  margin-top: 14px; padding-top: 14px; border-top: 1px solid ${p => p.theme.colors.inputGray}22;
  label { display: block; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary}; margin: 8px 0 5px; }
  input { width: 100%; padding: 10px 12px; box-sizing: border-box; background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.textPrimary}; border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: 8px; font-size: 0.9rem; }
`;
const TextBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; background: transparent; border: none; cursor: pointer;
  color: ${p => p.theme.colors.primary}; font-weight: 600; font-size: 0.86rem; padding: 6px 0;
`;
const CreateBtn = styled.button`
  margin-top: 10px; width: 100%; min-height: 42px; border: none; border-radius: 10px; cursor: pointer;
  background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 700; font-size: 0.88rem;
  &:disabled { opacity: 0.6; cursor: default; }
`;
const AddrRow = styled.button`
  display: flex; align-items: flex-start; gap: 10px; width: 100%; text-align: left;
  background: ${p => p.theme.colors.background};
  border: 1.5px solid ${p => p.$checked ? p.theme.colors.primary : p.theme.colors.inputGray + '22'};
  border-radius: 10px; padding: 10px 12px; cursor: pointer; & + & { margin-top: 8px; }
  &:disabled { opacity: 0.55; cursor: default; }
  &:hover:not(:disabled) { border-color: ${p => p.theme.colors.primary}; }
  .box { margin-top: 2px; color: ${p => p.$checked ? p.theme.colors.primary : p.theme.colors.inputGray}; flex-shrink: 0; }
  .lbl { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.88rem; }
  .txt { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; margin-top: 2px; word-break: break-word; }
  .on  { color: ${p => p.theme.colors.success}; font-size: 0.72rem; font-weight: 700; margin-top: 3px; display: inline-flex; align-items: center; gap: 5px; }
`;

const BackLink = styled.button`
  background: transparent; border: none; cursor: pointer; padding: 0 0 10px;
  color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const Loader = styled.div`text-align: center; padding: 30px; color: ${p => p.theme.colors.primary}; svg { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg);} }`;

const AddToRouteButton = ({ leadId, label = 'Add to Route', onAdded, className, hideTrigger = false, openSignal = 0 }) => {
  const [open, setOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  // routeId -> Set of this lead's address ids already on that route (null = primary)
  const [existing, setExisting] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });
  // Address-level state. `addresses` is the lead's pickable stops: the primary
  // (id null) plus each LeadAddress. `existing` maps routeId -> Set of address
  // ids already on that route, so the picker can grey out per address.
  const [addresses, setAddresses] = useState([]);
  const [pickRoute, setPickRoute] = useState(null);   // route awaiting an address choice
  const [picked, setPicked] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, mine, lead, extra] = await Promise.all([
        getRoutes(),
        getRoutesByLead(leadId).catch(() => []),
        // Callers pass only a lead id (the map pin popup has no address data),
        // so the primary address is fetched here rather than required as a prop.
        getLeadById(leadId).then(r => r?.data ?? r).catch(() => null),
        getLeadAddresses(leadId).then(r => r?.data ?? r).catch(() => []),
      ]);
      // Only today + future (and undated) routes — don't add leads to past runs.
      setRoutes(all.filter(r => isRouteUpcoming(r.date)));
      // routeId -> Set of this lead's address ids already on it (null = primary)
      setExisting(new Map(mine.map(r => [r.id, new Set(r.address_ids || [])])));

      const opts = [];
      if (lead) {
        opts.push({
          id: null,
          label: 'Primary',
          text: [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', '),
        });
      }
      (Array.isArray(extra) ? extra : []).forEach(a => opts.push({
        id: a.id,
        label: a.label || 'Address',
        text: [a.street, a.city, a.state, a.zipcode].filter(Boolean).join(', '),
      }));
      setAddresses(opts);
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to load routes', success: false });
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const openPicker = useCallback(() => {
    setShowNew(false); setNewName(''); setNewDate('');
    setPickRoute(null); setPicked(new Set());
    setOpen(true); load();
  }, [load]);

  // External trigger. Skips the initial 0 so the picker doesn't pop open on mount.
  useEffect(() => {
    if (openSignal) openPicker();
    // openPicker is stable enough here; re-running on it would reopen on every load() identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  // Address ids of this lead already on the given route.
  const onRoute = (routeId) => existing.get(routeId) || new Set();

  // Every address already there? Then the route row is fully done.
  const routeIsFull = (routeId) => {
    const have = onRoute(routeId);
    return addresses.length > 0 && addresses.every(a => have.has(a.id));
  };

  // Commit a set of address ids to a route.
  const commit = async (route, addressIds) => {
    setBusyId(route.id);
    try {
      await addStopsToRoute(route.id, addressIds.map(id => ({ lead_id: leadId, lead_address_id: id })));
      setExisting(prev => {
        const next = new Map(prev);
        const have = new Set(next.get(route.id) || []);
        addressIds.forEach(id => have.add(id));
        next.set(route.id, have);
        return next;
      });
      const n = addressIds.length;
      setFeedback({
        show: true,
        message: n > 1 ? `Added ${n} stops to "${route.name}"` : `Added to "${route.name}"`,
        success: true,
      });
      onAdded?.(route);
      setOpen(false);
    } catch (err) {
      setFeedback({ show: true, message: err.response?.data?.message || 'Failed to add to route', success: false });
    } finally {
      setBusyId(null);
      setPickRoute(null);
      setPicked(new Set());
    }
  };

  const addTo = async (route) => {
    // One address (or none loaded) -> no reason to ask; keep it a single click.
    if (addresses.length <= 1) {
      await commit(route, [addresses[0]?.id ?? null]);
      return;
    }
    // Otherwise choose which address(es) — pre-ticking nothing, since the ones
    // already on the route render as disabled + ticked.
    setPickRoute(route);
    setPicked(new Set());
  };

  const createAndAdd = async () => {
    if (!newName.trim()) { setFeedback({ show: true, message: 'Give the route a name', success: false }); return; }
    setBusyId('new');
    try {
      const route = await createRoute({ name: newName.trim(), date: newDate || null });
      if (addresses.length > 1) {
        // New route is empty, so go straight to choosing addresses.
        setRoutes(prev => [route, ...prev]);
        setShowNew(false);
        setPickRoute(route);
        setPicked(new Set());
        setBusyId(null);
        return;
      }
      await addStopsToRoute(route.id, [{ lead_id: leadId, lead_address_id: addresses[0]?.id ?? null }]);
      setFeedback({ show: true, message: `Created "${route.name}" and added the lead`, success: true });
      onAdded?.(route);
      setOpen(false);
    } catch (err) {
      setFeedback({ show: true, message: err.response?.data?.message || 'Failed to create route', success: false });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Trigger type="button" className={className} onClick={openPicker}>
          <FaRoute /> {label}
        </Trigger>
      )}

      <Modal title="Add to Route" isOpen={open} onCancel={() => setOpen(false)} hideFooter>
        {loading ? (
          <Loader><FaSpinner size={26} /></Loader>
        ) : pickRoute ? (
          /* Step 2 — which address(es) of this lead go on the chosen route.
             Only reached when the lead has more than one address. */
          <>
            <BackLink type="button" onClick={() => { setPickRoute(null); setPicked(new Set()); }}>
              ← Back to routes
            </BackLink>
            <p style={{ color: '#bbb', fontSize: '0.86rem', margin: '0 0 10px' }}>
              Which address on <strong style={{ color: '#fff' }}>{pickRoute.name}</strong>?
            </p>
            {addresses.map(a => {
              const already = onRoute(pickRoute.id).has(a.id);
              const checked = picked.has(a.id);
              return (
                <AddrRow
                  key={a.id ?? 'primary'}
                  type="button"
                  disabled={already}
                  $checked={checked}
                  onClick={() => setPicked(prev => {
                    const next = new Set(prev);
                    if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                    return next;
                  })}
                >
                  <span className="box">{already || checked ? <FaCheckSquare /> : <FaRegSquare />}</span>
                  <span>
                    <span className="lbl">{a.label}</span>
                    {a.text && <span className="txt" style={{ display: 'block' }}>{a.text}</span>}
                    {already && <span className="on"><FaCheck /> Already on this route</span>}
                  </span>
                </AddrRow>
              );
            })}
            <CreateBtn
              onClick={() => commit(pickRoute, [...picked])}
              disabled={picked.size === 0 || busyId === pickRoute.id}
            >
              {busyId === pickRoute.id
                ? 'Adding…'
                : picked.size > 1 ? `Add ${picked.size} stops` : 'Add stop'}
            </CreateBtn>
          </>
        ) : (
          <>
            {routes.length === 0 && !showNew && (
              <p style={{ color: '#bbb', fontSize: '0.9rem', margin: '4px 0 8px' }}>No routes yet — create one below.</p>
            )}
            {routes.map(route => {
              // "Added" now means every one of this lead's addresses is on the
              // route. A partially-added route stays clickable so the remaining
              // addresses can be added.
              const onIt = onRoute(route.id).size;
              const full = routeIsFull(route.id);
              return (
                <RouteRow key={route.id} disabled={full || busyId === route.id} onClick={() => !full && addTo(route)}>
                  <span>
                    <span className="name">{route.name}</span>
                    <span className="date"><FaCalendarDay size={10} /> {route.date ? `${routeDateRelative(route.date)} · ${formatRouteDate(route.date)}` : 'No date'} · {route.leadCount} stops</span>
                    {onIt > 0 && !full && (
                      <span className="date" style={{ color: '#84c959' }}>
                        <FaCheck size={9} /> {onIt} of this lead&apos;s {addresses.length} addresses already on it
                      </span>
                    )}
                  </span>
                  {full ? <span className="added"><FaCheck /> Added</span>
                    : busyId === route.id ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    : <FaPlus color="#F69620" />}
                </RouteRow>
              );
            })}

            {showNew ? (
              <NewBox>
                <label>New route name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. North Lowell — Monday" autoFocus />
                <label>Visit date</label>
                <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                <CreateBtn onClick={createAndAdd} disabled={busyId === 'new'}>
                  {busyId === 'new' ? 'Creating…' : 'Create & add lead'}
                </CreateBtn>
              </NewBox>
            ) : (
              <NewBox>
                <TextBtn onClick={() => setShowNew(true)}><FaPlus /> Create a new route</TextBtn>
              </NewBox>
            )}
          </>
        )}
      </Modal>

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </>
  );
};

export default AddToRouteButton;
